import { TwainGameId } from 'betgames-contract-js';
import {
    ILeftVsRightKeys,
    IParticipantsPair,
    IResults,
    IScores,
    ITournamentMatchShotData,
} from 'twain-sport-fe-mono/interfaces';
import { AudioService } from './AudioService';
import { getRandomFirstRank } from './utils/getParticipantRanks';
import { getOutroAudio } from './utils/getOutroAudio';
import { getGameStartAudio } from './utils/getGameStartAudio';
import { getInPlayAudio } from './utils/getInPlayAudio';
import { getRankIntro } from './utils/getRankAudio';
import { getRandomBadShotsAmount } from './utils/getRandomBadShotsAmount';

export class GameAudioService extends AudioService {
    times: Record<string, number> = {
        gameStartIntroAfter: 6,
        gameStartIntroUntil: 15,
        delayGameStart: 30,
        videoLatency: 1,
    };

    timer: number;

    participants: IParticipantsPair;

    wentLiveAt: number;

    leader: ILeftVsRightKeys = null;

    shotsPlayedSounds: string[] = [];

    private active = true;

    private getTimeSinceShot(): (time: number) => number {
        return (shotTime: number) =>
            Math.floor((shotTime - this.wentLiveAt) / 1000) - this.times.delayGameStart;
    }

    public toggleActivation(active: boolean): void {
        this.active = active;
    }

    public get isActive() {
        return this.active;
    }

    public playShot(
        gameId: TwainGameId,
        shotData: ITournamentMatchShotData,
        totals: IScores,
        history: IScores<ITournamentMatchShotData[]>,
        participants: IParticipantsPair,
    ): void {
        const leftShorts: ITournamentMatchShotData[] = history[participants?.left.code];
        const rightShorts: ITournamentMatchShotData[] = history[participants?.right.code];

        if (!leftShorts || !rightShorts || !this.active) {
            return;
        }

        const newLeader =
            totals[participants?.left.code] > totals[participants?.right.code] ? 'left' : 'right'; // TODO will trigger even for even count and shots with some delay
        const isEqualShots = leftShorts.length === rightShorts.length;
        const isLeaderChanged = this.leader && this.leader !== newLeader && isEqualShots;

        if (isLeaderChanged || (!this.leader && !isEqualShots)) {
            this.leader = newLeader;
        }
        const timeAfterStart = this.getTimeSinceShot()(shotData.time);
        const pointsDiff = Math.abs(
            totals[participants?.left.code] - totals[participants?.right.code],
        );

        // Play after 1 first shots from both sides
        if (leftShorts.length === 1 && rightShorts.length === 1) {
            this.play(this.sounds.inPlayStart[getGameStartAudio(leftShorts[0], rightShorts[0])]);
            return;
        }

        if (this.isPlaying) {
            return;
        }

        const inPlayAudio = getInPlayAudio(
            {
                timeAfterStart,
                pointsDiff,
                isLeaderChanged,
                newLeader,
                leftShorts,
                rightShorts,
            },
            this.shotsPlayedSounds,
            this.leader,
            this.getTimeSinceShot(),
        );
        if (inPlayAudio) {
            this.play(this.sounds.inPlay[inPlayAudio], () => {
                this.shotsPlayedSounds.push(inPlayAudio);
            });
        }
    }

    private getFirstMatchInTournament(): string {
        const randomSound = Math.floor(Math.random() * 5);
        return this.sounds.introFirstMatch[randomSound + 1];
    }

    private getPlayersIntro(participants: IParticipantsPair): string[] {
        const list: string[] = [];
        if (!participants) {
            return [];
        }

        const leftRankSound = getRandomFirstRank(participants.left);
        const rightRankSound = getRandomFirstRank(participants.right);

        if (leftRankSound) {
            list.push(this.sounds.intro.left[leftRankSound]);
        }
        if (rightRankSound) {
            list.push(this.sounds.intro.right[rightRankSound]);
        }

        if (participants.left.rank <= 8 && participants.right.rank <= 8) {
            list.push(this.sounds.introInTop);
        }

        if (list.length < 2) {
            return list;
        }

        return [
            ...list.splice(Math.floor(Math.random() * list.length), 1),
            ...list.splice(Math.floor(Math.random() * list.length), 1),
        ];
    }

    public playStartGame(
        gameId: TwainGameId,
        participants: IParticipantsPair,
        wentLiveAt: number,
        isFirstMatch: boolean,
    ): void {
        if (!this.active) {
            return;
        }

        if (this.timer) {
            window.clearInterval(this.timer);
            this.timer = null;
        }

        this.wentLiveAt = wentLiveAt;
        let list: string[] = [];

        if (isFirstMatch) {
            list.push(this.getFirstMatchInTournament());
        }

        // max 2 random participants audio
        list.push(...this.getPlayersIntro(participants));

        // 1 random rank audio
        list.push(this.sounds.introRanks[getRankIntro(participants)]);

        list = list.filter(Boolean);

        this.timer = window.setInterval(() => {
            const timeLeftFromLive =
                Math.round((Date.now() - wentLiveAt) / 1000) - this.times.videoLatency;

            // don't start playing intro later than 15 seconds
            if (timeLeftFromLive > this.times.gameStartIntroUntil) {
                window.clearInterval(this.timer);
                this.timer = null;
                return;
            }

            // don't start playing intro earlier than 6 seconds
            if (!this.isMuted && timeLeftFromLive > this.times.gameStartIntroAfter) {
                if (list.length) {
                    this.runPlaylist(list);
                }
                window.clearInterval(this.timer);
                this.timer = null;
            }
        }, 1000);
    }

    public playOnEndGame(
        gameId: TwainGameId,
        results: IResults,
        participants: IParticipantsPair,
        history: IScores<ITournamentMatchShotData[]>,
    ): void {
        if (!this.active) {
            return;
        }

        const left = results.participants[participants.left.code];
        const right = results.participants[participants.right.code];
        const pointsDiff = Math.abs(left.points - right.points);
        const list = [];

        const outroAudio = getOutroAudio(
            {
                left,
                right,
                pointsDiff,
                gameId,
                results,
            },
            history,
            participants,
        );

        const randomBadShotsAmountAudio = getRandomBadShotsAmount(gameId, left, right);
        if (randomBadShotsAmountAudio) {
            list.push(this.sounds.inPlay[randomBadShotsAmountAudio]);
        }
        if (outroAudio) {
            list.push(this.sounds.outro[outroAudio]);
        }

        if (list.length) {
            this.runPlaylist(list);
        }
    }

    public gameEnd(): void {
        if (this.timer) {
            window.clearInterval(this.timer);
            this.timer = null;
        }
        this.mute();
        this.shotsPlayedSounds = [];
        this.wentLiveAt = null;
        this.leader = null;

        this.refresh();
    }
}
